import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaMinus, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import './AgregarCompraModal.css';
import comprasService from '../../services/ComprasService';

const AgregarCompraModal = ({ isOpen, onClose, onCompraCreada }) => {
  const [formData, setFormData] = useState({
    idProveedor: '',
    referencia: '',
    fecha: new Date().toISOString().split('T')[0],
    esPagado: true
  });

  const [proveedores, setProveedores] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [opcionesIngredientes, setOpcionesIngredientes] = useState([]);
  const [detallesCompra, setDetallesCompra] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cargar datos iniciales
  useEffect(() => {
    if (isOpen) {
      cargarProveedores();
      cargarIngredientes();
    }
  }, [isOpen]);

  const cargarProveedores = async () => {
    try {
      const data = await comprasService.getProveedores();
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarIngredientes = async () => {
    try {
      const data = await comprasService.getIngredientes();
      setIngredientes(data);
      
      // Convertir ingredientes al formato de react-select
      const opciones = data.map(ingrediente => ({
        value: ingrediente.idIngrediente,
        label: ingrediente.nombre,
        costo: parseFloat(ingrediente.costo) || 0,
        nombreUnidad: ingrediente.nombreUnidad || '',
        prefijoUnidad: ingrediente.prefijoUnidad || ''
      }));
      
      setOpcionesIngredientes(opciones);
    } catch (error) {
      console.error('Error al cargar ingredientes:', error);
    }
  };

  // Manejar cambios en el formulario principal
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Agregar nuevo detalle de ingrediente
  const agregarDetalle = () => {
    const nuevoDetalle = {
      id: Date.now(), // ID temporal
      idIngrediente: '',
      nombre: '',
      cantidad: 1,
      costo: 0,
      importe: 0,
      ingredienteSeleccionado: null,
      nombreUnidad: '',
      prefijoUnidad: ''
    };
    setDetallesCompra(prev => [...prev, nuevoDetalle]);
  };

  // Eliminar detalle de ingrediente
  const eliminarDetalle = (id) => {
    setDetallesCompra(prev => prev.filter(detalle => detalle.id !== id));
  };

  // Manejar cambios en los detalles
  const handleDetalleChange = (id, field, value) => {
    setDetallesCompra(prev => prev.map(detalle => {
      if (detalle.id === id) {
        const updatedDetalle = { ...detalle, [field]: value };
        
        // Si cambió el ingrediente (react-select)
        if (field === 'ingredienteSeleccionado') {
          if (value) {
            updatedDetalle.idIngrediente = value.value;
            updatedDetalle.nombre = value.label;
            updatedDetalle.costo = value.costo;
            updatedDetalle.ingredienteSeleccionado = value;
            updatedDetalle.nombreUnidad = value.nombreUnidad;
            updatedDetalle.prefijoUnidad = value.prefijoUnidad;
          } else {
            updatedDetalle.idIngrediente = '';
            updatedDetalle.nombre = '';
            updatedDetalle.costo = 0;
            updatedDetalle.ingredienteSeleccionado = null;
            updatedDetalle.nombreUnidad = '';
            updatedDetalle.prefijoUnidad = '';
          }
        }
        
        // Recalcular importe
        if (field === 'cantidad' || field === 'costo' || field === 'ingredienteSeleccionado') {
          updatedDetalle.importe = updatedDetalle.cantidad * updatedDetalle.costo;
        }
        
        return updatedDetalle;
      }
      return detalle;
    }));
  };

  // Calcular total de la compra
  const calcularTotal = () => {
    return detallesCompra.reduce((total, detalle) => total + detalle.importe, 0);
  };

  // Guardar compra
  const guardarCompra = async () => {
    if (!formData.idProveedor || !formData.referencia || detallesCompra.length === 0) {
      setError('Por favor complete todos los campos requeridos y agregue al menos un ingrediente');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const compraData = {
        idProveedor: formData.idProveedor,
        referencia: formData.referencia,
        fecha: formatearFecha(formData.fecha),
        monto: calcularTotal(),
        status: formData.esPagado ? 'PAGADO' : 'PENDIENTE',
        detalles: detallesCompra.map(detalle => ({
          idIngrediente: detalle.idIngrediente,
          nombre: detalle.nombre,
          cantidad: detalle.cantidad,
          costo: detalle.costo,
          importe: detalle.importe
        }))
      };

      await comprasService.crearCompra(compraData);
      
      // Notificar éxito y cerrar modal
      onCompraCreada && onCompraCreada();
      cerrarModal();
      
    } catch (error) {
      setError('Error al crear la compra: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha para envío sin afectar zona horaria
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '';
    const [año, mes, dia] = fechaISO.split('-');
    return `${dia}/${mes}/${año}`;
  };

  // Cerrar modal y limpiar formulario
  const cerrarModal = () => {
    setFormData({
      idProveedor: '',
      referencia: '',
      fecha: new Date().toISOString().split('T')[0],
      esPagado: true
    });
    setDetallesCompra([]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container agregar-compra-modal">
        <div className="modal-header">
          <h2>Nueva Compra de Ingredientes</h2>
          <button className="btn-close" onClick={cerrarModal}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form className="compra-form">
            <div className="form-row">
              <div className="form-group">
                <label>Proveedor *</label>
                <select
                  name="idProveedor"
                  value={formData.idProveedor}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar proveedor</option>
                  {proveedores.map(proveedor => (
                    <option key={proveedor.idProveedor} value={proveedor.idProveedor}>
                      {proveedor.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Referencia *</label>
                <input
                  type="text"
                  name="referencia"
                  value={formData.referencia}
                  onChange={handleInputChange}
                  placeholder="Número de factura o referencia"
                  required
                />
              </div>

              <div className="form-group">
                <label>Fecha *</label>
                <div className="fecha-input-container">
                  <input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                    required
                    className="fecha-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="esPagado"
                    checked={formData.esPagado}
                    onChange={handleInputChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    {formData.esPagado ? 'PAGADO' : 'PENDIENTE'}
                  </span>
                </label>
              </div>
            </div>
          </form>

          <div className="detalles-section">
            <div className="detalles-header">
              <h3>Ingredientes</h3>
              <button 
                type="button" 
                className="btn-agregar-detalle"
                onClick={agregarDetalle}
              >
                <FaPlus /> Agregar Ingrediente
              </button>
            </div>

            {detallesCompra.length === 0 ? (
              <div className="sin-detalles">
                No hay ingredientes agregados. Haga clic en "Agregar Ingrediente" para comenzar.
              </div>
            ) : (
              <div className="detalles-tabla">
                <div className="tabla-header">
                  <div>Ingrediente</div>
                  <div>Unidad</div>
                  <div>Cantidad</div>
                  <div>Costo Unit.</div>
                  <div>Importe</div>
                  <div>Acciones</div>
                </div>

                {detallesCompra.map(detalle => (
                  <div key={detalle.id} className="tabla-row">
                    <div className="campo-ingrediente">
                      <Select
                        value={detalle.ingredienteSeleccionado}
                        onChange={(selectedOption) => handleDetalleChange(detalle.id, 'ingredienteSeleccionado', selectedOption)}
                        options={opcionesIngredientes}
                        placeholder="Buscar ingrediente..."
                        isSearchable={true}
                        isClearable={true}
                        noOptionsMessage={() => "No se encontraron ingredientes"}
                        className="select-ingrediente"
                        classNamePrefix="select"
                      />
                    </div>

                    <div className="campo-unidad">
                      {detalle.prefijoUnidad || '-'}
                    </div>

                    <div className="campo-cantidad">
                      <input
                        type="number"
                        value={detalle.cantidad}
                        onChange={(e) => handleDetalleChange(detalle.id, 'cantidad', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="campo-costo">
                      <input
                        type="number"
                        value={detalle.costo}
                        onChange={(e) => handleDetalleChange(detalle.id, 'costo', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="campo-importe">
                      ${detalle.importe.toFixed(2)}
                    </div>

                    <div className="campo-acciones">
                      <button
                        type="button"
                        className="btn-eliminar-detalle"
                        onClick={() => eliminarDetalle(detalle.id)}
                      >
                        <FaMinus />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="tabla-footer">
                  <div className="total-compra">
                    <strong>Total: ${calcularTotal().toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-cancelar" 
            onClick={cerrarModal}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn-guardar" 
            onClick={guardarCompra}
            disabled={loading || detallesCompra.length === 0}
          >
            <FaSave /> {loading ? 'Guardando...' : 'Guardar Compra'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgregarCompraModal;